import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useTournament } from "@/lib/TournamentContext";
import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin } from "lucide-react";
import TeamBadge from "@/components/app/TeamBadge";

const KoSlot = ({ team, label }) =>
  team ? (
    <div className="flex items-center gap-2 min-w-0">
      {team.crest && <img src={team.crest} alt="" className="w-5 h-5 object-contain shrink-0" />}
      <span className="font-display text-sm tracking-wider truncate">{team.shortName || team.name}</span>
    </div>
  ) : (
    <span className="text-xs text-zinc-500 italic truncate">{label}</span>
  );

const ProvisionalBracket = ({ ko }) => {
  if (!ko?.pairs?.length) return null;
  const half = Math.ceil(ko.pairs.length / 2);
  const columns = [ko.pairs.slice(0, half), ko.pairs.slice(half)];
  return (
    <div className="surface-card p-4 md:p-6 mb-8" data-testid="provisional-bracket">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-2xl uppercase tracking-tight">Knockouts · Round of 32</h2>
          <span className="text-[10px] uppercase tracking-widest bg-amber-500/20 text-amber-400 border border-amber-500/30 px-2 py-0.5">
            Provisional · As It Stands
          </span>
        </div>
        {!ko.thirds_resolved && (
          <span className="text-xs text-zinc-500">Best-3rd slots resolve once all 12 groups have played</span>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-3">
        {columns.map((col, ci) => (
          <div key={ci} className="space-y-3">
            {col.map((p, i) => (
              <div
                key={`${ci}-${i}`}
                data-testid={`ko-pair-${p.home_code}-${p.away_code}`}
                className="border border-white/10 bg-black/20 px-3 py-2.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <KoSlot team={p.home} label={p.home_label} />
                  <span className="text-[10px] text-zinc-600 uppercase tracking-widest shrink-0">vs</span>
                  <div className="flex-1 flex justify-end">
                    <KoSlot team={p.away} label={p.away_label} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Fixtures() {
  const { active } = useTournament();
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState({});
  const [ko, setKo] = useState(null);
  const [tab, setTab] = useState("fixtures"); // fixtures | results
  const [groupFilter, setGroupFilter] = useState("ALL");
  const [stageFilter, setStageFilter] = useState("ALL");
  const [matchdayFilter, setMatchdayFilter] = useState("ALL");

  useEffect(() => {
    if (!active) return;
    (async () => {
      const tid = active.tournament_id;
      const [mR, tR, kR] = await Promise.all([
        api.get(`/tournaments/${tid}/matches`),
        api.get(`/tournaments/${tid}/teams`),
        api.get(`/tournaments/${tid}/provisional-knockouts`).catch(() => ({ data: null })),
      ]);
      setMatches(mR.data);
      setTeams(Object.fromEntries(tR.data.map((t) => [t.team_id, t])));
      setKo(kR.data);
    })();
  }, [active]);

  const allGroups = useMemo(() => Array.from(new Set(matches.map((m) => m.group).filter(Boolean))).sort(), [matches]);
  const allStages = useMemo(() => Array.from(new Set(matches.map((m) => m.stage))), [matches]);
  const allMatchdays = useMemo(() => Array.from(new Set(matches.map((m) => m.matchday).filter((x) => x != null))).sort((a, b) => a - b), [matches]);

  const filtered = matches.filter((m) => {
    if (groupFilter !== "ALL" && m.group !== groupFilter) return false;
    if (stageFilter !== "ALL" && m.stage !== stageFilter) return false;
    if (matchdayFilter !== "ALL" && String(m.matchday) !== matchdayFilter) return false;
    if (tab === "fixtures") return m.status !== "completed";
    return m.status === "completed";
  });

  // Group by day
  const byDay = useMemo(() => {
    const map = {};
    filtered.forEach((m) => {
      const d = new Date(m.kickoff).toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
      (map[d] = map[d] || []).push(m);
    });
    return Object.entries(map).sort(([a], [b]) => new Date(filtered.find((m) => new Date(m.kickoff).toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" }) === a)?.kickoff) - new Date(filtered.find((m) => new Date(m.kickoff).toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" }) === b)?.kickoff));
  }, [filtered]);

  if (!active) return null;

  return (
    <div className="px-3 md:px-8 py-6 md:py-8 grid-bg min-h-[calc(100vh-65px)]">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-tag mb-1">Schedule</div>
          <h1 className="font-display text-4xl md:text-5xl uppercase">Fixtures & Results</h1>
        </div>
        <Link to="/predict" data-testid="cta-predict-from-fixtures" className="bg-primary-accent px-5 py-2.5 font-display tracking-widest uppercase hover:bg-[var(--primary-hover)]">
          Make Predictions →
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex gap-1 surface-card p-1">
          <button data-testid="tab-fixtures" onClick={() => setTab("fixtures")} className={`px-4 py-2 text-xs uppercase tracking-widest ${tab === "fixtures" ? "bg-primary-accent text-white" : "text-zinc-400 hover:text-white"}`}>Fixtures</button>
          <button data-testid="tab-results" onClick={() => setTab("results")} className={`px-4 py-2 text-xs uppercase tracking-widest ${tab === "results" ? "bg-primary-accent text-white" : "text-zinc-400 hover:text-white"}`}>Results</button>
        </div>

        <div className="flex items-center gap-2">
          <span className="label-tag">Stage</span>
          <select data-testid="stage-select" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
            className="bg-zinc-950 border border-zinc-800 px-3 py-1.5 text-sm focus:border-primary-accent focus:outline-none uppercase tracking-widest">
            <option value="ALL">All</option>
            {allStages.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </select>
        </div>

        {allGroups.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="label-tag">Group</span>
            <select data-testid="group-select" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}
              className="bg-zinc-950 border border-zinc-800 px-3 py-1.5 text-sm focus:border-primary-accent focus:outline-none uppercase tracking-widest">
              <option value="ALL">All</option>
              {allGroups.map((g) => <option key={g} value={g}>Group {g}</option>)}
            </select>
          </div>
        )}

        {allMatchdays.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="label-tag">Matchday</span>
            <select data-testid="matchday-select" value={matchdayFilter} onChange={(e) => setMatchdayFilter(e.target.value)}
              className="bg-zinc-950 border border-zinc-800 px-3 py-1.5 text-sm focus:border-primary-accent focus:outline-none uppercase tracking-widest">
              <option value="ALL">All</option>
              {allMatchdays.map((d) => <option key={d} value={String(d)}>MD {d}</option>)}
            </select>
          </div>
        )}

        <span className="text-xs text-zinc-500 ml-auto">{filtered.length} match{filtered.length !== 1 ? "es" : ""}</span>
      </div>

      {filtered.length === 0 && <div className="text-zinc-500 text-sm">No {tab} in this filter.</div>}

      {tab === "fixtures" && <ProvisionalBracket ko={ko} />}

      <div className="space-y-8">
        {byDay.map(([day, list]) => (
          <div key={day}>
            <div className="flex items-center gap-3 mb-3">
              <Calendar size={14} className="text-primary-accent"/>
              <h2 className="font-display text-xl uppercase tracking-tight">{day}</h2>
              <div className="flex-1 h-px bg-white/5"/>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {list.map((m) => {
                const h = teams[m.home_team_id] || {};
                const a = teams[m.away_team_id] || {};
                const completed = m.status === "completed";
                return (
                  <div key={m.match_id} className={`surface-card p-4 ${completed ? "" : "hover:border-white/30 transition-colors"}`} data-testid={`fixture-${m.match_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="label-tag">
                        {m.group ? `Group ${m.group}` : m.stage.toUpperCase()}
                        {m.matchday != null && <span className="ml-2 text-zinc-400">· MD {m.matchday}</span>}
                      </span>
                      {m.status === "live" ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-widest bg-primary-accent text-white">
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/> Live
                        </span>
                      ) : (
                        <span className="text-xs text-zinc-500 flex items-center gap-1">
                          <Clock size={11}/>
                          {new Date(m.kickoff).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 md:gap-3">
                      <div className="flex items-center gap-2 justify-end text-right min-w-0">
                        <TeamBadge team={h} size="md" className="justify-end" hideCodeOnMobile/>
                      </div>
                      <div className="font-display text-2xl md:text-3xl tabular-nums min-w-[50px] text-center">
                        {completed
                          ? <span><span className={m.home_score > m.away_score ? "text-primary-accent" : ""}>{m.home_score}</span> <span className="text-zinc-600">-</span> <span className={m.away_score > m.home_score ? "text-primary-accent" : ""}>{m.away_score}</span></span>
                          : <span className="text-zinc-600 text-xs tracking-widest">VS</span>}
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <TeamBadge team={a} size="md" hideCodeOnMobile/>
                      </div>
                    </div>
                    {completed && (
                      <div className="mt-3 pt-3 border-t border-white/5 text-xs uppercase tracking-widest text-primary-accent">
                        FT · {m.home_score > m.away_score ? `${h.name} won` : m.away_score > m.home_score ? `${a.name} won` : "Draw"}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
